import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { t } from '../i18n';
import { Avatar, LanguageToggle } from '../components/ui';
import { IconLayers, IconLibrary, IconOrbit, IconPlus, IconPulse, IconSearch, IconUser } from '../components/icons';

/** Dictionary keys, not labels: a module-level label would freeze the language of the tab bar. */
const TABS = [
  { to: '/app', key: 'nav.feed', icon: IconPulse, end: true },
  { to: '/app/library', key: 'nav.library', icon: IconLibrary, end: false },
  { to: '/app/playlists', key: 'nav.playlists', icon: IconLayers, end: false },
  { to: '/app/profile', key: 'nav.profile', icon: IconUser, end: false },
] as const;

export function AppShell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <>
      <a className="skip-link" href="#main">{t('nav.skip')}</a>
      <header className="topbar">
        <div className="topbar__inner">
          <Link className="brand" to="/app">
            <span className="brand__mark"><IconOrbit size={26} /></span>
            my-lib<span className="brand__space">space</span>
          </Link>
          <nav className="nav-links" aria-label={t('nav.sections')}>
            {TABS.map(tab => (
              <NavLink key={tab.to} to={tab.to} end={tab.end} className="nav-link">{t(tab.key)}</NavLink>
            ))}
          </nav>
          <div className="topbar__actions">
            <Link className="icon-btn" to="/app/search" aria-label={t('nav.search')}><IconSearch /></Link>
            <button type="button" className="btn btn--primary topbar__add" onClick={() => navigate('/app/library?add=1')}>
              <IconPlus size={18} /> {t('nav.add')}
            </button>
            {user ? (
              <Link className="topbar__profile" to="/app/profile" aria-label={t('nav.yourProfile')}>
                <Avatar name={user.displayName} src={user.avatarUrl} size="sm" />
              </Link>
            ) : null}
          </div>
        </div>
      </header>
      <main id="main" className="app-main" tabIndex={-1}><Outlet /></main>
      <nav className="tabbar" aria-label={t('nav.main')}>
        {TABS.map(tab => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className="tab">
            <span className="tab__icon"><tab.icon size={21} /></span>{t(tab.key)}
          </NavLink>
        ))}
      </nav>
      {!/\/app\/playlists\/[^/]+\/edit$/.test(pathname) ? (
        <Link className="fab" to="/app/library?add=1"><IconPlus size={19} /> {t('nav.addLink')}</Link>
      ) : null}
    </>
  );
}

export function PublicShell() {
  const { user } = useAuth();
  return (
    <>
      <a className="skip-link" href="#main">{t('nav.skip')}</a>
      <header className="topbar">
        <div className="topbar__inner">
          <Link className="brand" to={user ? '/app' : '/'}>
            <span className="brand__mark"><IconOrbit size={26} /></span>
            my-lib<span className="brand__space">space</span>
          </Link>
          <div className="topbar__actions">
            <LanguageToggle />
            {user ? <Link className="btn btn--outline btn--sm" to="/app">{t('nav.toLibrary')}</Link> : (
              <>
                <Link className="btn btn--ghost btn--sm" to="/login">{t('nav.enter')}</Link>
                <Link className="btn btn--primary btn--sm" to="/register">{t('nav.start')}</Link>
              </>
            )}
          </div>
        </div>
      </header>
      <main id="main" tabIndex={-1}><Outlet /></main>
      <footer className="page row row--between tiny muted public-footer">
        <span>my-lib.space</span><Link to="/">{t('nav.about')}</Link>
      </footer>
    </>
  );
}
